import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Modal } from "antd";
import { Alert } from "components/elements";

import useOptions from "features/masterData/hooks/useOptions";
import { generateRandomString, sortDays } from "utils/helper";
import {
  DEFAULT_SHIFTPERIOD,
  initialValues as INITIAL_VALUES,
} from "../schemas/shiftsSchema";
import * as services from "../services/shiftsApi";
import { setIsLoading, setShiftsList } from "../slices/attendanceSlice";

function toInitialValues(weekDayWorkingOptions = [], weekWorkingOptions = [], attendanceConditionOptions = []) {
  const weekday =
    weekDayWorkingOptions.find((w) => w.nameEn === "Week Day") || {};
  const option = weekWorkingOptions.find((o) => o.code === "ALLWEEK") || {};
  console.log("attendanceConditionOptions", attendanceConditionOptions);
  const normalShifts = [
    {
      ...DEFAULT_SHIFTPERIOD,
      workingDayId: weekday.id || null,
      optionId: option.id || null,
    },
  ];

  let attendanceConditionId = attendanceConditionOptions?.find?.(o => o?.nameEn == "By Shift Period")?.id;

  const advanceShifts = weekDayWorkingOptions
    .filter((o) => o.nameEn !== "Week Day")
    .sort(sortDays)
    .map((o) => ({
      ...DEFAULT_SHIFTPERIOD,
      workingDayId: o.id || null,
      optionId: option.id || null,
    }));

  const modifiedValues = {
    ...INITIAL_VALUES,
    attendanceConditionId,
    normalShifts,
    advanceShifts,
  };
  return modifiedValues;
}

function toParams(values = {}, weekDayWorkingOptions = []) {
  const { normalShifts = [], advanceShifts = [] } = values;
  let modifiedShiftPeriods = [];
  if (!values.advanceSetting) {
    modifiedShiftPeriods = weekDayWorkingOptions
      .filter((o) => o.nameEn !== "Week Day")
      .sort(sortDays)
      .map((o) => ({
        ...normalShifts[0],
        workingDayId: o.id,
      }));
  } else {
    modifiedShiftPeriods = [...advanceShifts];
  }
  const modifiedParams = {
    ...values,
    doubleShift: values.doubleShift ? "Y" : "N",
    nightShift: values.nightShift ? "Y" : "N",
    advanceSetting: values.advanceSetting ? "Y" : "N",
    shiftPeriods: modifiedShiftPeriods.map((s) => ({
      ...s,
      activateDeactivate: s.activateDeactivate ? "Y" : "N",
    })),
    preShiftOtPeriod: values.preShiftOtPeriod ? "Y" : "N",
    allowFlexibleWorkInStatus: values.allowFlexibleWorkInStatus ? "Y" : "N",
    calculateOtAuto: values.calculateOtAuto ? "Y" : "N",
    checkInChanelId: values.calculateOtAuto ? values.checkInChanelId : null,
    openAllowanceTab: values.openAllowanceTab ? "Y" : "N",
    specialShiftFromOt: values.specialShiftFromOt ? "Y" : "N",
    specialShiftFromHoliday: values.specialShiftFromHoliday ? "Y" : "N",
    workTimeAllowance: values.workTimeAllowance ? "Y" : "N",
    lateStatusDuringBreaksCalculation: values.lateStatusDuringBreaksCalculation
      ? "Y"
      : "N",
  };
  return modifiedParams;
}

function useShiftsCreate() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const { t } = useTranslation();
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const isLoading = useSelector((state) => state.attendance.isLoading);
  const shiftsList = useSelector((state) => state.attendance.shiftsList);

  const {
    attendanceConditionOptions = [],
    checkInChanelOptions = [],
    weekDayWorkingOptions = [],
    weekWorkingOptions = [],
    getAttendanceConditionOptions,
    getCheckInChanelOptions,
    getWeekDayWorkingOptions,
    getWeekWorkingOptions,
  } = useOptions();

  const initialValues = useMemo(() => {
    return toInitialValues(weekDayWorkingOptions, weekWorkingOptions, attendanceConditionOptions);
  }, [weekDayWorkingOptions, weekWorkingOptions, attendanceConditionOptions]);

  const modifiedShiftsList = useMemo(() => {
    const modifiedOptions = shiftsList.map((opt) => ({
      label: opt.shiftNameEn, //i18n.language === "en" ? opt.shiftNameEn : opt.shiftName,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [i18n.language, shiftsList]);

  const getAttendanceShifts = useCallback(async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await services.getAttendanceShiftsPaging({
        requestId: generateRandomString(),
        companyId: currentCompany,
        page: 0,
        size: 100,
      });
      dispatch(setShiftsList(response.data?.attendanceShiftResp || []));
    } catch (error) {
      Alert({ type: "error", resultObject: error });
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany, dispatch]);

  const onCancel = useCallback(() => {
    navigate("/configuration/attendance", { state: { selectedTab: 'shifts' } });
  }, [navigate]);

  const createAttendanceShifts = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        let result = await services.createAttendanceShifts(
          toParams(values, weekDayWorkingOptions)
        );
        Alert({ resultObject: result });
        onCancel();
      } catch (error) {
        Alert({ type: "error", resultObject: error });
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [weekDayWorkingOptions, dispatch, onCancel]
  );

  const onSubmit = useCallback(
    (values) => {
      const modifiedValues = { ...values, companyId: currentCompany };
      Modal.confirm({
        title: t('translation.confirmation'),
        content: (
          <>
            <p>{t('translation.are-you-sure-confirm-to-create')}</p>
          </>
        ),
        onOk() {
          createAttendanceShifts(modifiedValues);
        },
        okText: t('translation.confirm-0'),
        cancelText: t('translation.action.cancel'),
      });
    },
    [currentCompany, createAttendanceShifts]
  );

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getAttendanceShifts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany]);

  useEffect(() => {
    getAttendanceConditionOptions();
  }, [getAttendanceConditionOptions]);

  useEffect(() => {
    getCheckInChanelOptions();
  }, [getCheckInChanelOptions]);

  useEffect(() => {
    getWeekDayWorkingOptions();
  }, [getWeekDayWorkingOptions]);

  useEffect(() => {
    getWeekWorkingOptions();
  }, [getWeekWorkingOptions]);

  return {
    isLoading,
    initialValues,
    shiftsList: modifiedShiftsList,
    attendanceConditionOptions,
    checkInChanelOptions,
    weekDayWorkingOptions,
    weekWorkingOptions,
    onCancel,
    onSubmit,
  };
}

export default useShiftsCreate;
